import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import moment from 'moment';
import axios from 'axios';
import TimeOffHub from './TimeOffHub';
import FlexibleWorkingHub from './FlexibleWorkingHub';
import TrainingView from './TrainingView';

// ============================================================
// PORTAL AXIOS INSTANCE  — always uses employee_access_token,
// completely separate from the owner app's axiosConfig instance
// ============================================================
const portalApi = axios.create({ baseURL: '/api' });

portalApi.interceptors.request.use((config) => {
  const token = localStorage.getItem('employee_access_token');
  if (token) config.headers['Authorization'] = `Bearer ${token}`;
  return config;
});

// On 401, clear employee tokens and reload to login — don't attempt
// a refresh via the owner's refresh flow
portalApi.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('employee_access_token');
      localStorage.removeItem('employee_refresh_token');
      window.location.reload();
    }
    return Promise.reject(error);
  }
);

// ============================================================
// ICONS
// ============================================================
const Icons = {
  Home: () => (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"></path>
      <polyline points="9,22 9,12 15,12 15,22"></polyline>
    </svg>
  ),
  Calendar: () => (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect>
      <line x1="16" y1="2" x2="16" y2="6"></line>
      <line x1="8" y1="2" x2="8" y2="6"></line>
      <line x1="3" y1="10" x2="21" y2="10"></line>
    </svg>
  ),
  MessageCircle: () => (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z"></path>
    </svg>
  ),
  FileText: () => (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
      <polyline points="14,2 14,8 20,8"></polyline>
      <line x1="16" y1="13" x2="8" y2="13"></line>
      <line x1="16" y1="17" x2="8" y2="17"></line>
      <polyline points="10,9 9,9 8,9"></polyline>
    </svg>
  ),
  Clock: () => (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="10"></circle>
      <polyline points="12,6 12,12 16,14"></polyline>
    </svg>
  ),
  Target: () => (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="10"></circle>
      <circle cx="12" cy="12" r="6"></circle>
      <circle cx="12" cy="12" r="2"></circle>
    </svg>
  ),
  Heart: () => (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"></path>
    </svg>
  ),
  GraduationCap: () => (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M22 10v6M2 10l10-5 10 5-10 5z"></path>
      <path d="M6 12v5c3 3 9 3 12 0v-5"></path>
    </svg>
  ),
  Bell: () => (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"></path>
      <path d="M13.73 21a2 2 0 0 1-3.46 0"></path>
    </svg>
  ),
  ChevronRight: () => (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="9,18 15,12 9,6"></polyline>
    </svg>
  ),
  ArrowLeft: () => (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <line x1="19" y1="12" x2="5" y2="12"></line>
      <polyline points="12,19 5,12 12,5"></polyline>
    </svg>
  ),
  Plus: () => (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <line x1="12" y1="5" x2="12" y2="19"></line>
      <line x1="5" y1="12" x2="19" y2="12"></line>
    </svg>
  ),
  Check: () => (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="20,6 9,17 4,12"></polyline>
    </svg>
  ),
  AlertCircle: () => (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="10"></circle>
      <line x1="12" y1="8" x2="12" y2="12"></line>
      <line x1="12" y1="16" x2="12.01" y2="16"></line>
    </svg>
  ),
  Upload: () => (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
      <polyline points="17,8 12,3 7,8"></polyline>
      <line x1="12" y1="3" x2="12" y2="15"></line>
    </svg>
  ),
  Send: () => (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <line x1="22" y1="2" x2="11" y2="13"></line>
      <polygon points="22,2 15,22 11,13 2,9"></polygon>
    </svg>
  ),
  User: () => (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path>
      <circle cx="12" cy="7" r="4"></circle>
    </svg>
  ),
  LogOut: () => (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"></path>
      <polyline points="16,17 21,12 16,7"></polyline>
      <line x1="21" y1="12" x2="9" y2="12"></line>
    </svg>
  ),
  Coffee: () => (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M18 8h1a4 4 0 0 1 0 8h-1"></path>
      <path d="M2 8h16v9a4 4 0 0 1-4 4H6a4 4 0 0 1-4-4V8z"></path>
      <line x1="6" y1="1" x2="6" y2="4"></line>
      <line x1="10" y1="1" x2="10" y2="4"></line>
      <line x1="14" y1="1" x2="14" y2="4"></line>
    </svg>
  ),
};

// ============================================================
// SHARED STYLES
// ============================================================
const styles = {
  container: {
    minHeight: '100vh',
    background: '#f8fafc',
    fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
  },
  headerGradient: {
    background: 'linear-gradient(135deg, #0d9488 0%, #0891b2 50%, #0284c7 100%)',
    color: 'white',
    padding: '20px 16px',
    paddingTop: '24px',
  },
  card: {
    background: 'white',
    borderRadius: '16px',
    boxShadow: '0 1px 3px rgba(0,0,0,0.08), 0 4px 12px rgba(0,0,0,0.04)',
    padding: '20px',
    marginBottom: '12px',
    transition: 'transform 0.2s ease, box-shadow 0.2s ease',
  },
  cardInteractive: {
    cursor: 'pointer',
  },
  buttonPrimary: {
    background: 'linear-gradient(135deg, #0d9488 0%, #0891b2 100%)',
    color: 'white',
    border: 'none',
    borderRadius: '12px',
    padding: '14px 24px',
    fontWeight: '600',
    fontSize: '15px',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '8px',
  },
  buttonSecondary: {
    background: '#f1f5f9',
    color: '#475569',
    border: 'none',
    borderRadius: '12px',
    padding: '14px 24px',
    fontWeight: '600',
    fontSize: '15px',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
  },
  badge: {
    display: 'inline-flex',
    alignItems: 'center',
    padding: '4px 10px',
    borderRadius: '20px',
    fontSize: '12px',
    fontWeight: '600',
  },
  input: {
    width: '100%',
    padding: '14px 16px',
    border: '2px solid #e2e8f0',
    borderRadius: '12px',
    fontSize: '15px',
    outline: 'none',
    transition: 'border-color 0.2s ease, box-shadow 0.2s ease',
    boxSizing: 'border-box',
  },
  sectionHeader: {
    fontSize: '13px',
    fontWeight: '600',
    color: '#64748b',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
    marginBottom: '12px',
    marginTop: '24px',
  },
};

const pulseAnimation = `
  @keyframes pulse {
    0%, 100% { opacity: 1; }
    50% { opacity: 0.5; }
  }
  @keyframes spin {
    to { transform: rotate(360deg); }
  }
`;

// ============================================================
// SHARED SUB-COMPONENTS
// ============================================================
const QuickActionCard = ({ icon: Icon, title, subtitle, badge, badgeColor, onClick }) => {
  const [isHovered, setIsHovered] = useState(false);
  return (
    <div
      onClick={onClick}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      style={{
        ...styles.card,
        ...styles.cardInteractive,
        display: 'flex',
        alignItems: 'center',
        gap: '16px',
        transform: isHovered ? 'translateY(-2px)' : 'none',
        boxShadow: isHovered
          ? '0 4px 12px rgba(0,0,0,0.12), 0 8px 24px rgba(0,0,0,0.08)'
          : styles.card.boxShadow,
      }}
    >
      <div style={{
        width: '48px',
        height: '48px',
        borderRadius: '12px',
        background: 'linear-gradient(135deg, #0d9488 0%, #0891b2 100%)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: 'white',
        flexShrink: 0,
      }}>
        <Icon />
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontWeight: '600', color: '#1e293b', fontSize: '15px', marginBottom: '2px' }}>
          {title}
        </div>
        <div style={{ fontSize: '13px', color: '#64748b', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
          {subtitle}
        </div>
      </div>
      {badge !== undefined && badge !== null && (
        <div style={{
          ...styles.badge,
          background: badgeColor || '#dbeafe',
          color: badgeColor === '#dcfce7' ? '#166534' : badgeColor === '#fef3c7' ? '#92400e' : '#1e40af',
        }}>
          {badge}
        </div>
      )}
      <div style={{ color: '#94a3b8' }}><Icons.ChevronRight /></div>
    </div>
  );
};

const StatCard = ({ label, value, subtext }) => (
  <div style={{
    background: 'rgba(255,255,255,0.15)',
    backdropFilter: 'blur(10px)',
    borderRadius: '12px',
    padding: '16px',
    flex: 1,
    minWidth: '140px',
  }}>
    <div style={{ fontSize: '12px', opacity: 0.9, marginBottom: '4px' }}>{label}</div>
    <div style={{ fontSize: '24px', fontWeight: '700' }}>{value}</div>
    {subtext && <div style={{ fontSize: '11px', opacity: 0.8, marginTop: '2px' }}>{subtext}</div>}
  </div>
);

const BackButton = ({ onClick }) => (
  <button
    onClick={onClick}
    style={{
      width: '40px',
      height: '40px',
      borderRadius: '50%',
      background: 'rgba(255,255,255,0.2)',
      border: 'none',
      cursor: 'pointer',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      color: 'white',
    }}
  >
    <Icons.ArrowLeft />
  </button>
);

// ============================================================
// PORTAL FETCH HELPER  (uses employee_access_token)
// ============================================================
const portalFetch = async (url, options = {}) => {
  const token = localStorage.getItem('employee_access_token');
  return fetch(`/api${url}`, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`,
      ...options.headers,
    },
  });
};

// ============================================================
// CLOCK WIDGET  — lifted to module scope so it is never
// unmounted by a parent re-render
// ============================================================
const ClockWidget = ({ compact = false, onStatusChange }) => {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [error, setError] = useState(null);
  const [currentTime, setCurrentTime] = useState(new Date());

  // Tick the clock every second
  useEffect(() => {
    const tick = setInterval(() => setCurrentTime(new Date()), 1000);
    return () => clearInterval(tick);
  }, []);

  const fetchStatus = useCallback(async () => {
    try {
      const response = await portalFetch('/clock/status');
      const data = await response.json();
      if (data.success) {
        setStatus(data);
        onStatusChange?.(data);
      }
    } catch (err) {
      console.error('Error fetching clock status:', err);
      if (err.message !== '404') setError('Could not fetch clock status');
    } finally {
      setLoading(false);
    }
  }, [onStatusChange]);

  // Initial fetch + 30-second poll
  useEffect(() => {
    fetchStatus();
    const interval = setInterval(fetchStatus, 30000);
    return () => clearInterval(interval);
  }, [fetchStatus]);

  const getLocation = () =>
    new Promise((resolve, reject) => {
      if (!navigator.geolocation) { reject(new Error('Geolocation not supported')); return; }
      navigator.geolocation.getCurrentPosition(
        (pos) => resolve({ latitude: pos.coords.latitude, longitude: pos.coords.longitude, accuracy: pos.coords.accuracy }),
        (err) => reject(err),
        { enableHighAccuracy: true, timeout: 10000, maximumAge: 60000 }
      );
    });

  const handleClockIn = async () => {
    setActionLoading(true);
    setError(null);
    try {
      let locationData = {};
      if (status?.location_required || status?.salon_location_set) {
        try {
          locationData = await getLocation();
        } catch (locErr) {
          if (status?.location_required) {
            setError('Location required. Please enable location services.');
            setActionLoading(false);
            return;
          }
        }
      }
      const response = await portalFetch('/clock/in', {
        method: 'POST',
        body: JSON.stringify({ ...locationData, method: 'app' }),
      });
      const data = await response.json();
      if (data.success) await fetchStatus();
    } catch (err) {
      setError(err.message || 'Failed to clock in');
    } finally {
      setActionLoading(false);
    }
  };

  const handleClockOut = async () => {
    setActionLoading(true);
    setError(null);
    try {
      let locationData = {};
      try { locationData = await getLocation(); } catch { /* optional */ }
      const response = await portalFetch('/clock/out', {
        method: 'POST',
        body: JSON.stringify(locationData),
      });
      const data = await response.json();
      if (data.success) {
        setStatus(prev => ({ ...prev, clocked_in: false, last_shift_summary: data }));
        setTimeout(() => fetchStatus(), 3000);
      }
    } catch (err) {
      setError(err.message || 'Failed to clock out');
    } finally {
      setActionLoading(false);
    }
  };

  const handleStartBreak = async () => {
    setActionLoading(true);
    try {
      await portalFetch('/clock/break/start', { method: 'POST', body: JSON.stringify({}) });
      await fetchStatus();
    } catch (err) {
      setError(err.message || 'Failed to start break');
    } finally {
      setActionLoading(false);
    }
  };

  const handleEndBreak = async () => {
    setActionLoading(true);
    try {
      await portalFetch('/clock/break/end', { method: 'POST', body: JSON.stringify({}) });
      await fetchStatus();
    } catch (err) {
      setError(err.message || 'Failed to end break');
    } finally {
      setActionLoading(false);
    }
  };

  if (loading) return null;
  if (!status?.clock_enabled) return null;

  // ---- COMPACT (dashboard) ----
  if (compact) {
    return (
      <div style={{
        ...styles.card,
        background: status?.clocked_in ? 'linear-gradient(135deg, #ecfdf5 0%, #f0fdfa 100%)' : 'white',
        border: status?.clocked_in ? '2px solid #10b981' : '1px solid #e2e8f0',
        marginBottom: '16px',
      }}>
        {error && (
          <div style={{ background: '#fef2f2', color: '#dc2626', padding: '8px 12px', borderRadius: '8px', marginBottom: '12px', fontSize: '13px' }}>
            {error}
          </div>
        )}

        {status?.clocked_in ? (
          <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <div style={{ width: '12px', height: '12px', borderRadius: '50%', background: status.on_break ? '#f59e0b' : '#10b981', animation: 'pulse 2s infinite' }} />
                <div>
                  <div style={{ fontWeight: '600', color: '#1e293b', fontSize: '15px' }}>
                    {status.on_break ? '☕ On Break' : '✓ Clocked In'}
                  </div>
                  <div style={{ fontSize: '13px', color: '#64748b' }}>
                    Since {new Date(status.clock_in).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}
                  </div>
                </div>
              </div>
              <div style={{ textAlign: 'right' }}>
                <div style={{ fontSize: '24px', fontWeight: '700', color: '#0d9488' }}>{status.time_on_shift}</div>
                {status.break_minutes > 0 && <div style={{ fontSize: '12px', color: '#64748b' }}>{status.break_minutes}m break</div>}
              </div>
            </div>
            <div style={{ display: 'flex', gap: '10px' }}>
              {status.on_break ? (
                <button onClick={handleEndBreak} disabled={actionLoading} style={{ flex: 1, padding: '12px', background: '#fef3c7', color: '#b45309', border: 'none', borderRadius: '10px', fontWeight: '600', fontSize: '14px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px' }}>
                  {actionLoading ? '...' : '← End Break'}
                </button>
              ) : (
                <button onClick={handleStartBreak} disabled={actionLoading} style={{ flex: 1, padding: '12px', background: '#f1f5f9', color: '#475569', border: 'none', borderRadius: '10px', fontWeight: '600', fontSize: '14px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px' }}>
                  <Icons.Coffee /> Break
                </button>
              )}
              <button onClick={handleClockOut} disabled={actionLoading || status.on_break} style={{ flex: 1, padding: '12px', background: status.on_break ? '#f1f5f9' : '#fee2e2', color: status.on_break ? '#94a3b8' : '#dc2626', border: 'none', borderRadius: '10px', fontWeight: '600', fontSize: '14px', cursor: status.on_break ? 'not-allowed' : 'pointer', opacity: status.on_break ? 0.6 : 1 }}>
                {actionLoading ? '...' : 'Clock Out'}
              </button>
            </div>
            {status.on_break && <div style={{ textAlign: 'center', marginTop: '8px', fontSize: '12px', color: '#f59e0b' }}>End break before clocking out</div>}
            {status.late_arrival && <div style={{ marginTop: '8px', padding: '6px 10px', background: '#fef3c7', borderRadius: '6px', fontSize: '12px', color: '#b45309', textAlign: 'center' }}>Late arrival recorded</div>}
          </div>
        ) : (
          <div>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '12px' }}>
              <div>
                <div style={{ fontWeight: '600', color: '#1e293b', fontSize: '15px' }}>Ready to start?</div>
                {status?.scheduled_shift?.start && (
                  <div style={{ fontSize: '13px', color: '#64748b' }}>
                    Shift: {status.scheduled_shift.start} - {status.scheduled_shift.end}
                  </div>
                )}
              </div>
              <div style={{ fontSize: '24px', fontWeight: '700', color: '#64748b' }}>
                {currentTime.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}
              </div>
            </div>
            <button onClick={handleClockIn} disabled={actionLoading} style={{ width: '100%', padding: '16px', background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)', color: 'white', border: 'none', borderRadius: '12px', fontWeight: '600', fontSize: '16px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}>
              {actionLoading ? 'Clocking In...' : <><Icons.Clock /> Clock In</>}
            </button>
            {status?.location_required && <div style={{ marginTop: '8px', fontSize: '12px', color: '#64748b', textAlign: 'center' }}>📍 Location verification enabled</div>}
          </div>
        )}

        {status?.last_shift_summary && (
          <div style={{ marginTop: '12px', padding: '12px', background: '#dcfce7', borderRadius: '10px', textAlign: 'center' }}>
            <div style={{ fontWeight: '600', color: '#166534' }}>Shift Complete! ✓</div>
            <div style={{ fontSize: '14px', color: '#166534' }}>Total: {status.last_shift_summary.total_worked}</div>
          </div>
        )}
      </div>
    );
  }

  // ---- FULL (standalone) ----
  return (
    <div style={{ background: 'white', borderRadius: '16px', padding: '24px', boxShadow: '0 2px 8px rgba(0,0,0,0.08)', textAlign: 'center', maxWidth: '360px', margin: '0 auto' }}>
      <div style={{ fontSize: '48px', fontWeight: '700', color: '#1e293b', letterSpacing: '-1px' }}>
        {currentTime.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}
      </div>
      <div style={{ fontSize: '14px', color: '#64748b', marginBottom: '24px' }}>
        {currentTime.toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'long' })}
      </div>
      {error && <div style={{ background: '#fef2f2', color: '#dc2626', padding: '12px', borderRadius: '8px', marginBottom: '16px', fontSize: '14px' }}>{error}</div>}

      {!status?.clocked_in && (
        <div>
          {status?.scheduled_shift?.start && (
            <div style={{ background: '#f8fafc', padding: '12px 16px', borderRadius: '8px', marginBottom: '20px' }}>
              <div style={{ fontSize: '12px', color: '#64748b', marginBottom: '4px' }}>Today's shift</div>
              <div style={{ fontSize: '16px', fontWeight: '600', color: '#334155' }}>{status.scheduled_shift.start} - {status.scheduled_shift.end}</div>
            </div>
          )}
          <button onClick={handleClockIn} disabled={actionLoading} style={{ width: '100%', padding: '20px', fontSize: '18px', fontWeight: '600', background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)', color: 'white', border: 'none', borderRadius: '12px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '10px' }}>
            {actionLoading ? 'Clocking In...' : <><Icons.Clock /> Clock In</>}
          </button>
          {status?.location_required && <div style={{ marginTop: '12px', fontSize: '13px', color: '#64748b' }}>📍 Location verification required</div>}
        </div>
      )}

      {status?.clocked_in && (
        <div>
          <div style={{ display: 'inline-block', padding: '8px 16px', borderRadius: '20px', color: 'white', fontWeight: '600', fontSize: '14px', marginBottom: '20px', background: status.on_break ? '#f59e0b' : '#10b981' }}>
            {status.on_break ? '☕ On Break' : '✓ Clocked In'}
          </div>
          <div style={{ marginBottom: '16px' }}>
            <div style={{ fontSize: '12px', color: '#64748b', marginBottom: '4px' }}>Time on shift</div>
            <div style={{ fontSize: '36px', fontWeight: '700', color: '#1e293b' }}>{status.time_on_shift}</div>
          </div>
          {status.break_minutes > 0 && <div style={{ fontSize: '13px', color: '#64748b', marginBottom: '16px' }}>Break taken: {status.break_minutes} mins</div>}
          <div style={{ display: 'flex', gap: '12px', marginTop: '20px' }}>
            {status.on_break ? (
              <button onClick={handleEndBreak} disabled={actionLoading} style={{ flex: 1, padding: '14px', fontSize: '14px', fontWeight: '600', background: '#fef3c7', color: '#b45309', border: 'none', borderRadius: '10px', cursor: 'pointer' }}>
                {actionLoading ? 'Ending...' : 'End Break'}
              </button>
            ) : (
              <button onClick={handleStartBreak} disabled={actionLoading} style={{ flex: 1, padding: '14px', fontSize: '14px', fontWeight: '600', background: '#f8fafc', color: '#475569', border: '1px solid #e2e8f0', borderRadius: '10px', cursor: 'pointer' }}>
                {actionLoading ? '...' : '☕ Take Break'}
              </button>
            )}
            <button onClick={handleClockOut} disabled={actionLoading || status.on_break} style={{ flex: 1, padding: '14px', fontSize: '14px', fontWeight: '600', background: '#fee2e2', color: '#dc2626', border: 'none', borderRadius: '10px', cursor: 'pointer', opacity: status.on_break ? 0.5 : 1 }}>
              {actionLoading ? 'Clocking Out...' : 'Clock Out'}
            </button>
          </div>
          {status.on_break && <div style={{ marginTop: '12px', fontSize: '12px', color: '#f59e0b' }}>End your break before clocking out</div>}
        </div>
      )}

      {status?.last_shift_summary && (
        <div style={{ background: '#f0fdf4', padding: '20px', borderRadius: '12px', marginTop: '16px' }}>
          <div style={{ fontSize: '18px', fontWeight: '700', color: '#166534', marginBottom: '8px' }}>Shift Complete! ✓</div>
          <div style={{ fontSize: '14px', color: '#166534' }}>Total worked: {status.last_shift_summary.total_worked}</div>
        </div>
      )}
    </div>
  );
};

// ============================================================
// LOGIN VIEW  — lifted outside EmployeePortal
// ============================================================
const LoginView = ({ onLoginSuccess }) => {
  const [pin, setPin] = useState('');
  const [salonCode, setSalonCode] = useState('');
  const [loginLoading, setLoginLoading] = useState(false);
  const [loginError, setLoginError] = useState(null);

  const handleLogin = async () => {
    setLoginLoading(true);
    setLoginError(null);
    try {
      const response = await portalApi.post('/documents/training/employee/login', { pin, salon_code: salonCode });
      const data = response.data;
      if (data.success) {
        localStorage.setItem('employee_access_token', data.access_token);
        localStorage.setItem('employee_refresh_token', data.refresh_token);
        onLoginSuccess(data);
      } else {
        setLoginError(data.error || 'Invalid PIN or salon code');
      }
    } catch (error) {
      setLoginError(error.response?.data?.error || 'Connection error. Please check your internet connection.');
    }
    setLoginLoading(false);
  };

  return (
    <div style={{
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #0d9488 0%, #0891b2 50%, #0284c7 100%)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '16px',
      fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
    }}>
      <style>{pulseAnimation}</style>
      <div style={{ background: 'white', borderRadius: '24px', boxShadow: '0 25px 50px rgba(0,0,0,0.25)', padding: '40px 32px', width: '100%', maxWidth: '400px' }}>
        <div style={{ textAlign: 'center', marginBottom: '32px' }}>
          <div style={{ width: '72px', height: '72px', borderRadius: '20px', background: 'linear-gradient(135deg, #0d9488 0%, #0891b2 100%)', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 20px', fontSize: '32px' }}>👤</div>
          <h1 style={{ fontSize: '24px', fontWeight: '700', color: '#1e293b', margin: '0 0 8px 0' }}>Employee Portal</h1>
          <p style={{ color: '#64748b', margin: 0, fontSize: '15px' }}>Enter your details to access your dashboard</p>
        </div>

        {loginError && (
          <div style={{ background: '#fef2f2', border: '1px solid #fecaca', borderRadius: '12px', padding: '14px 16px', marginBottom: '20px', display: 'flex', alignItems: 'center', gap: '10px' }}>
            <Icons.AlertCircle />
            <p style={{ color: '#dc2626', fontSize: '14px', margin: 0 }}>{loginError}</p>
          </div>
        )}

        <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
          <div>
            <label style={{ display: 'block', fontSize: '14px', fontWeight: '600', color: '#334155', marginBottom: '8px' }}>Your PIN</label>
            <input type="password" value={pin} onChange={(e) => setPin(e.target.value)} style={{ ...styles.input, fontSize: '18px', textAlign: 'center', letterSpacing: '8px' }} placeholder="••••••" maxLength={6} inputMode="numeric" />
          </div>
          <div>
            <label style={{ display: 'block', fontSize: '14px', fontWeight: '600', color: '#334155', marginBottom: '8px' }}>Salon Code</label>
            <input type="text" value={salonCode} onChange={(e) => setSalonCode(e.target.value)} style={styles.input} placeholder="Enter your salon name or code" />
          </div>
          <button onClick={handleLogin} disabled={loginLoading || !pin || !salonCode} style={{ ...styles.buttonPrimary, width: '100%', padding: '16px', fontSize: '16px', marginTop: '8px', opacity: loginLoading || !pin || !salonCode ? 0.6 : 1, cursor: loginLoading || !pin || !salonCode ? 'not-allowed' : 'pointer' }}>
            {loginLoading ? 'Signing in...' : 'Sign In'}
          </button>
        </div>

        <div style={{ marginTop: '24px', paddingTop: '24px', borderTop: '1px solid #e2e8f0', textAlign: 'center' }}>
          <p style={{ color: '#94a3b8', fontSize: '13px', margin: 0 }}>Need help? Contact your manager for your PIN</p>
        </div>
      </div>
    </div>
  );
};

// ============================================================
// DASHBOARD VIEW  — lifted outside EmployeePortal
// ============================================================
const DashboardView = ({ employee, dashboardData, onNavigate, onClockStatusChange }) => {
  const getGreeting = () => {
    const h = new Date().getHours();
    if (h < 12) return 'Good morning';
    if (h < 17) return 'Good afternoon';
    return 'Good evening';
  };

  return (
    <div style={styles.container}>
      <style>{pulseAnimation}</style>
      <div style={styles.headerGradient}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '20px' }}>
          <div>
            <div style={{ fontSize: '14px', opacity: 0.9, marginBottom: '4px' }}>{getGreeting()}</div>
            <h1 style={{ fontSize: '24px', fontWeight: '700', margin: 0, marginBottom: '4px' }}>
              {employee?.firstName} {employee?.lastName}
            </h1>
            <div style={{ fontSize: '14px', opacity: 0.9 }}>{employee?.salonName}</div>
          </div>
          <button onClick={() => onNavigate('profile')} style={{ width: '44px', height: '44px', borderRadius: '50%', background: 'rgba(255,255,255,0.2)', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'white' }}>
            <Icons.User />
          </button>
        </div>
        <div style={{ display: 'flex', gap: '12px', overflowX: 'auto', paddingBottom: '4px', marginBottom: '-8px' }}>
          <StatCard label="Holiday Balance" value={`${dashboardData?.holidayBalance?.toFixed(1) || 0}`} subtext="days available" />
          <StatCard label="Next Shift" value={dashboardData?.nextShift?.time || '--:--'} subtext={dashboardData?.nextShift?.date || 'No shifts scheduled'} />
        </div>
      </div>

      <div style={{ padding: '16px', paddingTop: '24px' }}>
        {/* ClockWidget stays mounted as long as DashboardView is mounted */}
        <ClockWidget compact={true} onStatusChange={onClockStatusChange} />

        {(dashboardData?.pendingDocuments > 0 || dashboardData?.unreadMessages > 0) && (
          <>
            <div style={styles.sectionHeader}>Needs Attention</div>
            {dashboardData?.pendingDocuments > 0 && (
              <QuickActionCard icon={Icons.FileText} title="Documents to Sign" subtitle={`${dashboardData.pendingDocuments} document${dashboardData.pendingDocuments > 1 ? 's' : ''} awaiting signature`} badge={dashboardData.pendingDocuments} badgeColor="#fef3c7" onClick={() => onNavigate('documents')} />
            )}
            {dashboardData?.unreadMessages > 0 && (
              <QuickActionCard icon={Icons.MessageCircle} title="New Messages" subtitle="You have unread announcements" badge={dashboardData.unreadMessages} badgeColor="#dbeafe" onClick={() => onNavigate('messages')} />
            )}
          </>
        )}

        <div style={styles.sectionHeader}>Quick Actions</div>
        <QuickActionCard icon={Icons.Calendar} title="Time Off" subtitle={`${dashboardData?.holidayBalance?.toFixed(1) || 0} days remaining`} onClick={() => onNavigate('timeoff')} />
        <QuickActionCard icon={Icons.Clock} title="My Rota" subtitle="View your schedule" onClick={() => onNavigate('rota')} />
        <QuickActionCard icon={Icons.MessageCircle} title="Messages" subtitle="Announcements & updates" badge={dashboardData?.unreadMessages || null} badgeColor="#dbeafe" onClick={() => onNavigate('messages')} />
        <QuickActionCard icon={Icons.FileText} title="Documents" subtitle="View & sign documents" badge={dashboardData?.pendingDocuments || null} badgeColor="#fef3c7" onClick={() => onNavigate('documents')} />
        <QuickActionCard icon={Icons.Clock} title="Flexible Working" subtitle="Request changes to your pattern" onClick={() => onNavigate('flexibleworking')} />
        <QuickActionCard icon={Icons.Target} title="My Targets" subtitle="View performance goals" badge="Soon" badgeColor="#f1f5f9" onClick={() => onNavigate('targets')} />
        <QuickActionCard icon={Icons.GraduationCap} title="Training" subtitle="Complete your training modules" onClick={() => onNavigate('training')} />
      </div>
    </div>
  );
};

// ============================================================
// HOLIDAYS VIEW  — lifted outside EmployeePortal
// ============================================================
const HolidaysView = ({ employee, dashboardData, onBack, onDataRefresh }) => {
  const [activeTab, setActiveTab] = useState('overview');
  const [formData, setFormData] = useState({ startDate: '', endDate: '', notes: '' });
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const [submitSuccess, setSubmitSuccess] = useState(false);

  const calculateDays = () => {
    if (!formData.startDate || !formData.endDate) return 0;
    return Math.max(0, moment(formData.endDate).diff(moment(formData.startDate), 'days') + 1);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setSubmitError(null);
    setSubmitSuccess(false);
    try {
      await portalApi.post('/holidays', {
        secure_id: employee?.secureId,
        startDate: formData.startDate,
        endDate: formData.endDate,
        numberOfDaysHoliday: calculateDays(),
        notes: formData.notes,
      });
      setSubmitSuccess(true);
      setFormData({ startDate: '', endDate: '', notes: '' });
      await onDataRefresh();
      setTimeout(() => { setActiveTab('requests'); setSubmitSuccess(false); }, 2000);
    } catch (err) {
      setSubmitError('Connection error. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const cancelHoliday = async (holidayId) => {
    if (!window.confirm('Are you sure you want to cancel this holiday request?')) return;
    try {
      await portalApi.post(`/holidays/${holidayId}/cancel`);
      await onDataRefresh();
    } catch (err) {
      console.error('Failed to cancel holiday:', err);
    }
  };

  const getStatusStyle = (status) => {
    switch (status) {
      case 'APPROVED': return { background: '#dcfce7', color: '#166534' };
      case 'PENDING': return { background: '#fef3c7', color: '#92400e' };
      case 'REJECTED':
      case 'DENIED': return { background: '#fee2e2', color: '#991b1b' };
      case 'CANCELLED': return { background: '#f1f5f9', color: '#475569' };
      default: return { background: '#f1f5f9', color: '#475569' };
    }
  };

  return (
    <div style={styles.container}>
      <div style={styles.headerGradient}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
          <BackButton onClick={onBack} />
          <div>
            <h1 style={{ fontSize: '20px', fontWeight: '700', margin: 0 }}>Holidays</h1>
            <div style={{ fontSize: '14px', opacity: 0.9 }}>Manage your time off</div>
          </div>
        </div>
        <div style={{ display: 'flex', gap: '12px', marginTop: '20px' }}>
          <StatCard label="Available" value={dashboardData?.holidayBalance?.toFixed(1) || '0'} subtext="days" />
          <StatCard label="Taken" value={dashboardData?.takenDays?.toFixed(1) || '0'} subtext="days" />
          <StatCard label="Pending" value={dashboardData?.pendingDays?.toFixed(1) || '0'} subtext="days" />
        </div>
      </div>

      <div style={{ display: 'flex', background: 'white', borderBottom: '1px solid #e2e8f0', position: 'sticky', top: 0, zIndex: 10 }}>
        {['overview', 'request', 'requests'].map((tab) => (
          <button key={tab} onClick={() => setActiveTab(tab)} style={{ flex: 1, padding: '16px', border: 'none', background: 'none', cursor: 'pointer', fontSize: '14px', fontWeight: '600', color: activeTab === tab ? '#0d9488' : '#64748b', borderBottom: activeTab === tab ? '2px solid #0d9488' : '2px solid transparent' }}>
            {tab === 'overview' && 'Overview'}{tab === 'request' && 'New Request'}{tab === 'requests' && 'My Requests'}
          </button>
        ))}
      </div>

      <div style={{ padding: '16px' }}>
        {activeTab === 'overview' && (
          <>
            {dashboardData?.accruedDays !== undefined && (
              <div style={{ ...styles.card, background: 'linear-gradient(135deg, #f0fdfa 0%, #ecfeff 100%)', border: '1px solid #99f6e4' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '12px' }}>
                  <div style={{ width: '40px', height: '40px', borderRadius: '10px', background: '#0d9488', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'white' }}><Icons.Calendar /></div>
                  <div>
                    <div style={{ fontWeight: '600', color: '#134e4a' }}>Holiday Accrual</div>
                    <div style={{ fontSize: '13px', color: '#0f766e' }}>{dashboardData.accruedDays.toFixed(1)} days earned so far this year</div>
                  </div>
                </div>
                <div style={{ background: '#ccfbf1', borderRadius: '8px', height: '8px', overflow: 'hidden' }}>
                  <div style={{ background: '#0d9488', height: '100%', width: `${(dashboardData.accruedDays / dashboardData.totalDays) * 100}%`, transition: 'width 0.5s ease' }} />
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '8px', fontSize: '12px', color: '#0f766e' }}>
                  <span>Accrued: {dashboardData.accruedDays.toFixed(1)}</span>
                  <span>Annual: {dashboardData.totalDays.toFixed(1)}</span>
                </div>
              </div>
            )}
            <button onClick={() => setActiveTab('request')} style={{ ...styles.buttonPrimary, width: '100%', marginTop: '16px' }}>
              <Icons.Plus /> Request Holiday
            </button>
          </>
        )}

        {activeTab === 'request' && (
          <form onSubmit={handleSubmit}>
            {submitSuccess && (
              <div style={{ ...styles.card, background: '#dcfce7', border: '1px solid #86efac', display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '16px' }}>
                <Icons.Check /><span style={{ color: '#166534' }}>Holiday request submitted successfully!</span>
              </div>
            )}
            {submitError && (
              <div style={{ ...styles.card, background: '#fef2f2', border: '1px solid #fecaca', display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '16px' }}>
                <Icons.AlertCircle /><span style={{ color: '#991b1b' }}>{submitError}</span>
              </div>
            )}
            {dashboardData?.holidayBalance <= 0 && (
              <div style={{ ...styles.card, background: '#fef2f2', border: '1px solid #fecaca', display: 'flex', alignItems: 'center', gap: '12px' }}>
                <Icons.AlertCircle /><span style={{ color: '#991b1b', fontSize: '14px' }}>No holiday days remaining. Please contact your manager.</span>
              </div>
            )}
            <div style={{ marginBottom: '16px' }}>
              <label style={{ display: 'block', fontSize: '14px', fontWeight: '600', color: '#334155', marginBottom: '8px' }}>Start Date</label>
              <input type="date" value={formData.startDate} onChange={(e) => setFormData({ ...formData, startDate: e.target.value })} min={moment().format('YYYY-MM-DD')} required style={styles.input} />
            </div>
            <div style={{ marginBottom: '16px' }}>
              <label style={{ display: 'block', fontSize: '14px', fontWeight: '600', color: '#334155', marginBottom: '8px' }}>End Date</label>
              <input type="date" value={formData.endDate} onChange={(e) => setFormData({ ...formData, endDate: e.target.value })} min={formData.startDate || moment().format('YYYY-MM-DD')} required style={styles.input} />
            </div>
            {calculateDays() > 0 && (
              <div style={{ ...styles.card, background: '#f0fdfa', border: '1px solid #99f6e4', textAlign: 'center', marginBottom: '16px' }}>
                <div style={{ fontSize: '32px', fontWeight: '700', color: '#0d9488' }}>{calculateDays()}</div>
                <div style={{ color: '#0f766e', fontSize: '14px' }}>day{calculateDays() > 1 ? 's' : ''} requested</div>
                {calculateDays() > dashboardData?.holidayBalance && <div style={{ color: '#dc2626', fontSize: '13px', marginTop: '8px' }}>⚠️ Exceeds available balance</div>}
              </div>
            )}
            <div style={{ marginBottom: '24px' }}>
              <label style={{ display: 'block', fontSize: '14px', fontWeight: '600', color: '#334155', marginBottom: '8px' }}>Notes (Optional)</label>
              <textarea value={formData.notes} onChange={(e) => setFormData({ ...formData, notes: e.target.value })} placeholder="Add any additional information..." rows={3} style={{ ...styles.input, resize: 'vertical', minHeight: '100px' }} />
            </div>
            <button type="submit" disabled={submitting || !formData.startDate || !formData.endDate || calculateDays() > dashboardData?.holidayBalance} style={{ ...styles.buttonPrimary, width: '100%', opacity: submitting || !formData.startDate || !formData.endDate || calculateDays() > dashboardData?.holidayBalance ? 0.5 : 1, cursor: submitting || !formData.startDate || !formData.endDate || calculateDays() > dashboardData?.holidayBalance ? 'not-allowed' : 'pointer' }}>
              {submitting ? 'Submitting...' : 'Submit Request'}
            </button>
          </form>
        )}

        {activeTab === 'requests' && (
          <>
            {!dashboardData?.holidayRequests?.length ? (
              <div style={{ textAlign: 'center', padding: '48px 16px', color: '#64748b' }}>
                <div style={{ fontSize: '48px', marginBottom: '16px' }}>📅</div>
                <div style={{ fontWeight: '600', marginBottom: '8px' }}>No Requests Yet</div>
                <div style={{ fontSize: '14px' }}>Your holiday requests will appear here</div>
              </div>
            ) : (
              dashboardData.holidayRequests.map((request) => (
                <div key={request.id} style={styles.card}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '12px' }}>
                    <div>
                      <div style={{ fontWeight: '600', color: '#1e293b' }}>{moment(request.start_date).format('D MMM')} - {moment(request.end_date).format('D MMM YYYY')}</div>
                      <div style={{ fontSize: '13px', color: '#64748b' }}>{request.number_of_days_holiday} day{request.number_of_days_holiday > 1 ? 's' : ''}</div>
                    </div>
                    <span style={{ ...styles.badge, ...getStatusStyle(request.status) }}>{request.status}</span>
                  </div>
                  {request.notes && <div style={{ fontSize: '13px', color: '#64748b', padding: '8px 12px', background: '#f8fafc', borderRadius: '8px', marginBottom: '12px' }}>{request.notes}</div>}
                  {request.status === 'PENDING' && (
                    <button onClick={() => cancelHoliday(request.id)} style={{ background: 'none', border: '1px solid #e2e8f0', borderRadius: '8px', padding: '8px 16px', fontSize: '13px', color: '#dc2626', cursor: 'pointer' }}>
                      Cancel Request
                    </button>
                  )}
                </div>
              ))
            )}
          </>
        )}
      </div>
    </div>
  );
};

// ============================================================
// ROTA VIEW  — lifted outside EmployeePortal
// ============================================================
const RotaView = ({ onBack }) => {
  const [selectedWeek, setSelectedWeek] = useState(moment().startOf('isoWeek'));
  const [schedule, setSchedule] = useState([]);
  const [weeklyHours, setWeeklyHours] = useState(0);
  const [loadingSchedule, setLoadingSchedule] = useState(true);

  useEffect(() => { loadSchedule(); }, [selectedWeek]);

  const loadSchedule = async () => {
    setLoadingSchedule(true);
    try {
      const response = await portalApi.get(`/schedule/my/week?start_date=${selectedWeek.format('YYYY-MM-DD')}`);
      setSchedule(response.data.schedule || []);
      setWeeklyHours(response.data.weekly_hours || 0);
    } catch {
      setSchedule(buildEmptyWeek());
    } finally {
      setLoadingSchedule(false);
    }
  };

  const buildEmptyWeek = () =>
    ['Monday','Tuesday','Wednesday','Thursday','Friday','Saturday','Sunday'].map((name, i) => ({
      day_of_week: i, day_name: name,
      date: selectedWeek.clone().add(i, 'days').format('YYYY-MM-DD'),
      is_working: false, start_time: null, end_time: null, break_minutes: 0, hours_worked: 0,
    }));

  const weekDays = schedule.length > 0 ? schedule : buildEmptyWeek();

  const formatTime = (t) => { if (!t) return null; const p = t.split(':'); return `${p[0]}:${p[1]}`; };

  return (
    <div style={styles.container}>
      <style>{pulseAnimation}</style>
      <div style={styles.headerGradient}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
          <BackButton onClick={onBack} />
          <div style={{ flex: 1 }}>
            <h1 style={{ fontSize: '20px', fontWeight: '700', margin: 0 }}>My Rota</h1>
            <div style={{ fontSize: '14px', opacity: 0.9 }}>Week of {selectedWeek.format('D MMM YYYY')}</div>
          </div>
          <div style={{ background: 'rgba(255,255,255,0.2)', borderRadius: '20px', padding: '8px 16px', fontSize: '14px', fontWeight: '600' }}>{weeklyHours}h/week</div>
        </div>
      </div>

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 16px', background: 'white', borderBottom: '1px solid #e2e8f0', position: 'sticky', top: 0, zIndex: 10 }}>
        <button onClick={() => setSelectedWeek(selectedWeek.clone().subtract(1, 'week'))} style={{ ...styles.buttonSecondary, padding: '10px 16px', fontSize: '14px' }}>← Prev</button>
        <button onClick={() => setSelectedWeek(moment().startOf('isoWeek'))} style={{ background: 'transparent', border: 'none', padding: '10px 16px', fontSize: '14px', color: '#0d9488', fontWeight: '600', cursor: 'pointer' }}>This Week</button>
        <button onClick={() => setSelectedWeek(selectedWeek.clone().add(1, 'week'))} style={{ ...styles.buttonSecondary, padding: '10px 16px', fontSize: '14px' }}>Next →</button>
      </div>

      {loadingSchedule ? (
        <div style={{ display: 'flex', justifyContent: 'center', padding: '48px' }}>
          <div style={{ width: '32px', height: '32px', border: '3px solid #e2e8f0', borderTopColor: '#0d9488', borderRadius: '50%', animation: 'spin 1s linear infinite' }} />
        </div>
      ) : (
        <div style={{ padding: '16px' }}>
          {weekDays.map((day, index) => {
            const dayDate = day.date ? moment(day.date) : selectedWeek.clone().add(index, 'days');
            const isToday = dayDate.isSame(moment(), 'day');
            const isPast = dayDate.isBefore(moment(), 'day');
            return (
              <div key={day.day_of_week ?? index} style={{ ...styles.card, border: isToday ? '2px solid #0d9488' : '1px solid #e2e8f0', background: isToday ? '#f0fdfa' : isPast ? '#f8fafc' : 'white', opacity: isPast ? 0.7 : 1 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
                      <span style={{ fontSize: '15px', fontWeight: '600', color: isToday ? '#0d9488' : '#1e293b' }}>{day.day_name || dayDate.format('dddd')}</span>
                      {isToday && <span style={{ ...styles.badge, background: '#0d9488', color: 'white', fontSize: '10px', padding: '2px 8px' }}>TODAY</span>}
                    </div>
                    <div style={{ fontSize: '13px', color: '#64748b' }}>{dayDate.format('D MMMM')}</div>
                  </div>
                  {day.is_working ? (
                    <div style={{ textAlign: 'right' }}>
                      <div style={{ fontSize: '18px', fontWeight: '700', color: '#0d9488', marginBottom: '2px' }}>{formatTime(day.start_time)} - {formatTime(day.end_time)}</div>
                      <div style={{ fontSize: '12px', color: '#64748b', display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: '12px' }}>
                        {day.break_minutes > 0 && <span>☕ {day.break_minutes}m break</span>}
                        <span style={{ fontWeight: '500' }}>{day.hours_worked?.toFixed(1) || 0}h</span>
                      </div>
                    </div>
                  ) : (
                    <span style={{ ...styles.badge, background: '#f1f5f9', color: '#64748b', padding: '8px 16px' }}>Day Off</span>
                  )}
                </div>
              </div>
            );
          })}
          <div style={{ ...styles.card, background: 'linear-gradient(135deg, #f0fdfa 0%, #ecfeff 100%)', border: '1px solid #99f6e4', marginTop: '8px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div>
                <div style={{ fontSize: '13px', color: '#0f766e', marginBottom: '2px' }}>This Week</div>
                <div style={{ fontWeight: '600', color: '#134e4a' }}>{weekDays.filter(d => d.is_working).length} working days</div>
              </div>
              <div style={{ textAlign: 'right' }}>
                <div style={{ fontSize: '13px', color: '#0f766e', marginBottom: '2px' }}>Total Hours</div>
                <div style={{ fontSize: '24px', fontWeight: '700', color: '#0d9488' }}>{weeklyHours}h</div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// ============================================================
// MESSAGES VIEW  — lifted outside EmployeePortal
// ============================================================
const MessagesView = ({ onBack, onDataRefresh }) => {
  const [messages, setMessages] = useState([]);
  const [selectedMessage, setSelectedMessage] = useState(null);
  const [loadingMessages, setLoadingMessages] = useState(true);

  useEffect(() => { loadMessages(); }, []);

  const loadMessages = async () => {
    setLoadingMessages(true);
    try {
      const response = await portalApi.get('/messages/?limit=50');
      setMessages(Array.isArray(response.data) ? response.data : []);
    } catch { /* ignore */ }
    finally { setLoadingMessages(false); }
  };

  const markAsRead = async (secureId) => {
    try {
      await portalApi.put(`/messages/${secureId}/read`);
      setMessages(prev => prev.map(m => m.secure_id === secureId ? { ...m, read_at: new Date().toISOString() } : m));
      onDataRefresh?.();
    } catch { /* ignore */ }
  };

  const deleteMessage = async (secureId) => {
    if (!window.confirm('Are you sure you want to delete this message?')) return;
    try {
      await portalApi.delete(`/messages/${secureId}`);
      setMessages(prev => prev.filter(m => m.secure_id !== secureId));
      setSelectedMessage(null);
    } catch { /* ignore */ }
  };

  if (selectedMessage) {
    return (
      <div style={styles.container}>
        <div style={styles.headerGradient}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
            <BackButton onClick={() => setSelectedMessage(null)} />
            <div style={{ flex: 1 }}>
              <h1 style={{ fontSize: '18px', fontWeight: '700', margin: 0 }}>{selectedMessage.title || selectedMessage.subject || 'Message'}</h1>
              <div style={{ fontSize: '14px', opacity: 0.9 }}>{selectedMessage.type || 'Announcement'}</div>
            </div>
          </div>
        </div>
        <div style={{ padding: '16px' }}>
          <div style={{ fontSize: '13px', color: '#64748b', marginBottom: '16px' }}>{moment(selectedMessage.created_at).format('dddd, D MMMM YYYY [at] HH:mm')}</div>
          <div style={{ ...styles.card, whiteSpace: 'pre-wrap', lineHeight: '1.6', color: '#334155' }}>{selectedMessage.content || selectedMessage.body}</div>
          <button onClick={() => deleteMessage(selectedMessage.secure_id)} style={{ ...styles.buttonSecondary, width: '100%', marginTop: '16px', color: '#dc2626' }}>Delete Message</button>
        </div>
      </div>
    );
  }

  if (loadingMessages) {
    return (
      <div style={styles.container}>
        <style>{pulseAnimation}</style>
        <div style={styles.headerGradient}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
            <BackButton onClick={onBack} />
            <h1 style={{ fontSize: '20px', fontWeight: '700', margin: 0 }}>Messages</h1>
          </div>
        </div>
        <div style={{ display: 'flex', justifyContent: 'center', padding: '48px' }}>
          <div style={{ width: '32px', height: '32px', border: '3px solid #e2e8f0', borderTopColor: '#0d9488', borderRadius: '50%', animation: 'spin 1s linear infinite' }} />
        </div>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      <div style={styles.headerGradient}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
          <BackButton onClick={onBack} />
          <div>
            <h1 style={{ fontSize: '20px', fontWeight: '700', margin: 0 }}>Messages</h1>
            <div style={{ fontSize: '14px', opacity: 0.9 }}>Announcements & updates</div>
          </div>
        </div>
      </div>
      <div style={{ padding: '16px' }}>
        {messages.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '48px 16px', color: '#64748b' }}>
            <div style={{ fontSize: '48px', marginBottom: '16px' }}>📬</div>
            <div style={{ fontWeight: '600', marginBottom: '8px' }}>No Messages</div>
            <div style={{ fontSize: '14px' }}>You're all caught up!</div>
          </div>
        ) : (
          messages.map((message) => (
            <div key={message.id || message.secure_id} onClick={() => { setSelectedMessage(message); if (!message.read_at) markAsRead(message.secure_id); }} style={{ ...styles.card, ...styles.cardInteractive, borderLeft: !message.read_at ? '4px solid #0d9488' : 'none', background: !message.read_at ? '#f0fdfa' : 'white' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: !message.read_at ? '700' : '600', color: '#1e293b', marginBottom: '4px' }}>{message.title || message.subject || 'Message'}</div>
                  <div style={{ fontSize: '13px', color: '#64748b', marginBottom: '8px' }}>{message.type || 'Announcement'}</div>
                  <div style={{ fontSize: '14px', color: '#475569', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>{message.content || message.body}</div>
                </div>
                <div style={{ fontSize: '12px', color: '#94a3b8', whiteSpace: 'nowrap', marginLeft: '16px' }}>{moment(message.created_at).fromNow()}</div>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

// ============================================================
// DOCUMENTS VIEW  — lifted outside EmployeePortal
// ============================================================
const DocumentsView = ({ onBack, onDataRefresh }) => {
  const [documents, setDocuments] = useState([]);

  useEffect(() => { loadDocuments(); }, []);

  const loadDocuments = async () => {
    try {
      const response = await portalApi.get('/documents/employee/pending');
      if (response.data.success) setDocuments(response.data.documents || []);
    } catch { /* ignore */ }
  };

  const signDocument = async (documentId) => {
    try {
      await portalApi.post(`/documents/${documentId}/sign`);
      loadDocuments();
      onDataRefresh?.();
    } catch { /* ignore */ }
  };

  return (
    <div style={styles.container}>
      <div style={styles.headerGradient}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
          <BackButton onClick={onBack} />
          <div>
            <h1 style={{ fontSize: '20px', fontWeight: '700', margin: 0 }}>Documents</h1>
            <div style={{ fontSize: '14px', opacity: 0.9 }}>View & sign documents</div>
          </div>
        </div>
      </div>
      <div style={{ padding: '16px' }}>
        {documents.filter(d => !d.signed_at).length > 0 && (
          <>
            <div style={styles.sectionHeader}>Awaiting Signature</div>
            {documents.filter(d => !d.signed_at).map((doc) => (
              <div key={doc.id} style={{ ...styles.card, borderLeft: '4px solid #f59e0b' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <div>
                    <div style={{ fontWeight: '600', color: '#1e293b' }}>{doc.title}</div>
                    <div style={{ fontSize: '13px', color: '#64748b' }}>Assigned {moment(doc.assigned_at).fromNow()}</div>
                  </div>
                  <button onClick={() => signDocument(doc.id)} style={{ ...styles.buttonPrimary, padding: '10px 20px' }}>Sign</button>
                </div>
              </div>
            ))}
          </>
        )}
        {documents.filter(d => d.signed_at).length > 0 && (
          <>
            <div style={styles.sectionHeader}>Signed Documents</div>
            {documents.filter(d => d.signed_at).map((doc) => (
              <div key={doc.id} style={styles.card}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <div>
                    <div style={{ fontWeight: '600', color: '#1e293b' }}>{doc.title}</div>
                    <div style={{ fontSize: '13px', color: '#64748b' }}>Signed {moment(doc.signed_at).format('D MMM YYYY')}</div>
                  </div>
                  <span style={{ ...styles.badge, background: '#dcfce7', color: '#166534' }}><Icons.Check /> Signed</span>
                </div>
              </div>
            ))}
          </>
        )}
        {documents.length === 0 && (
          <div style={{ textAlign: 'center', padding: '48px 16px', color: '#64748b' }}>
            <div style={{ fontSize: '48px', marginBottom: '16px' }}>📄</div>
            <div style={{ fontWeight: '600', marginBottom: '8px' }}>No Documents</div>
            <div style={{ fontSize: '14px' }}>Nothing to sign at the moment</div>
          </div>
        )}
      </div>
    </div>
  );
};

// ============================================================
// FIT NOTE VIEW  — lifted outside EmployeePortal
// ============================================================
const FitNoteView = ({ employee, onBack }) => {
  const [formData, setFormData] = useState({ startDate: '', endDate: '', reason: '', file: null });
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [submitError, setSubmitError] = useState(null);

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 10 * 1024 * 1024) { setSubmitError('File size must be less than 10MB'); return; }
      setFormData({ ...formData, file });
      setSubmitError(null);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setSubmitError(null);
    try {
      const fd = new FormData();
      fd.append('secure_id', employee?.secureId);
      fd.append('start_date', formData.startDate);
      fd.append('end_date', formData.endDate);
      fd.append('absence_type', 'SICK');
      fd.append('reason', formData.reason);
      if (formData.file) fd.append('document', formData.file);
      await portalApi.post('/absences/submit', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      setSuccess(true);
      setFormData({ startDate: '', endDate: '', reason: '', file: null });
    } catch (err) {
      setSubmitError(err.response?.data?.message || 'Failed to submit fit note');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div style={styles.container}>
      <div style={styles.headerGradient}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
          <BackButton onClick={onBack} />
          <div>
            <h1 style={{ fontSize: '20px', fontWeight: '700', margin: 0 }}>Submit Fit Note</h1>
            <div style={{ fontSize: '14px', opacity: 0.9 }}>Upload sickness documentation</div>
          </div>
        </div>
      </div>
      <div style={{ padding: '16px' }}>
        {success && <div style={{ ...styles.card, background: '#dcfce7', border: '1px solid #86efac', display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '16px' }}><Icons.Check /><span style={{ color: '#166534' }}>Fit note submitted successfully. Your manager will be notified.</span></div>}
        {submitError && <div style={{ ...styles.card, background: '#fef2f2', border: '1px solid #fecaca', display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '16px' }}><Icons.AlertCircle /><span style={{ color: '#991b1b' }}>{submitError}</span></div>}
        <div style={{ ...styles.card, background: '#eff6ff', border: '1px solid #bfdbfe', marginBottom: '24px' }}>
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: '12px' }}>
            <Icons.AlertCircle />
            <div style={{ fontSize: '14px', color: '#1e40af' }}>If you're unwell and need to submit a fit note (sick note) from your doctor, please upload it here. Your manager will be notified automatically.</div>
          </div>
        </div>
        <form onSubmit={handleSubmit}>
          <div style={{ marginBottom: '16px' }}>
            <label style={{ display: 'block', fontSize: '14px', fontWeight: '600', color: '#334155', marginBottom: '8px' }}>Absence Start Date</label>
            <input type="date" value={formData.startDate} onChange={(e) => setFormData({ ...formData, startDate: e.target.value })} required style={styles.input} />
          </div>
          <div style={{ marginBottom: '16px' }}>
            <label style={{ display: 'block', fontSize: '14px', fontWeight: '600', color: '#334155', marginBottom: '8px' }}>Expected Return Date</label>
            <input type="date" value={formData.endDate} onChange={(e) => setFormData({ ...formData, endDate: e.target.value })} min={formData.startDate} required style={styles.input} />
          </div>
          <div style={{ marginBottom: '16px' }}>
            <label style={{ display: 'block', fontSize: '14px', fontWeight: '600', color: '#334155', marginBottom: '8px' }}>Reason for Absence</label>
            <textarea value={formData.reason} onChange={(e) => setFormData({ ...formData, reason: e.target.value })} placeholder="Brief description of your illness..." rows={3} required style={{ ...styles.input, resize: 'vertical', minHeight: '100px' }} />
          </div>
          <div style={{ marginBottom: '24px' }}>
            <label style={{ display: 'block', fontSize: '14px', fontWeight: '600', color: '#334155', marginBottom: '8px' }}>Upload Fit Note (if available)</label>
            <div style={{ border: '2px dashed #cbd5e1', borderRadius: '12px', padding: '24px', textAlign: 'center', cursor: 'pointer' }}>
              <input type="file" id="fitnote-upload" accept=".pdf,.jpg,.jpeg,.png" onChange={handleFileChange} style={{ display: 'none' }} />
              <label htmlFor="fitnote-upload" style={{ cursor: 'pointer' }}>
                <div style={{ color: '#64748b', marginBottom: '8px' }}><Icons.Upload /></div>
                {formData.file ? (
                  <div style={{ color: '#0d9488', fontWeight: '600' }}>{formData.file.name}</div>
                ) : (
                  <>
                    <div style={{ color: '#334155', fontWeight: '500' }}>Click to upload or drag and drop</div>
                    <div style={{ fontSize: '13px', color: '#94a3b8' }}>PDF, JPG or PNG (max 10MB)</div>
                  </>
                )}
              </label>
            </div>
          </div>
          <button type="submit" disabled={submitting || !formData.startDate || !formData.endDate || !formData.reason} style={{ ...styles.buttonPrimary, width: '100%', opacity: submitting || !formData.startDate || !formData.endDate || !formData.reason ? 0.5 : 1 }}>
            {submitting ? 'Submitting...' : 'Submit Fit Note'}
          </button>
        </form>
      </div>
    </div>
  );
};

// ============================================================
// TARGETS VIEW  — lifted outside EmployeePortal
// ============================================================
const TargetsView = ({ onBack }) => (
  <div style={styles.container}>
    <div style={styles.headerGradient}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
        <BackButton onClick={onBack} />
        <div>
          <h1 style={{ fontSize: '20px', fontWeight: '700', margin: 0 }}>My Targets</h1>
          <div style={{ fontSize: '14px', opacity: 0.9 }}>Performance goals & progress</div>
        </div>
      </div>
    </div>
    <div style={{ padding: '16px', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: '60vh' }}>
      <div style={{ fontSize: '80px', marginBottom: '24px' }}>🎯</div>
      <h2 style={{ fontSize: '24px', fontWeight: '700', color: '#1e293b', marginBottom: '8px', textAlign: 'center' }}>Coming Soon</h2>
      <p style={{ color: '#64748b', textAlign: 'center', maxWidth: '300px', lineHeight: '1.6' }}>Track your sales targets, service goals, and performance metrics. This feature is currently being developed.</p>
    </div>
  </div>
);

// ============================================================
// PROFILE VIEW  — lifted outside EmployeePortal
// ============================================================
const ProfileView = ({ employee, onBack, onLogout }) => (
  <div style={styles.container}>
    <div style={styles.headerGradient}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
        <BackButton onClick={onBack} />
        <div>
          <h1 style={{ fontSize: '20px', fontWeight: '700', margin: 0 }}>Profile</h1>
          <div style={{ fontSize: '14px', opacity: 0.9 }}>Your account</div>
        </div>
      </div>
    </div>
    <div style={{ padding: '16px' }}>
      <div style={{ ...styles.card, display: 'flex', alignItems: 'center', gap: '16px' }}>
        <div style={{ width: '64px', height: '64px', borderRadius: '50%', background: 'linear-gradient(135deg, #0d9488 0%, #0891b2 100%)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'white', fontSize: '24px', fontWeight: '700' }}>
          {employee?.firstName?.[0]}{employee?.lastName?.[0]}
        </div>
        <div>
          <div style={{ fontWeight: '600', fontSize: '18px', color: '#1e293b' }}>{employee?.firstName} {employee?.lastName}</div>
          <div style={{ color: '#64748b' }}>{employee?.salonName}</div>
        </div>
      </div>
      <div style={{ marginTop: '24px' }}>
        <button onClick={onLogout} style={{ ...styles.card, display: 'flex', alignItems: 'center', gap: '16px', width: '100%', textAlign: 'left', border: 'none', cursor: 'pointer' }}>
          <div style={{ width: '40px', height: '40px', borderRadius: '10px', background: '#fee2e2', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#dc2626' }}><Icons.LogOut /></div>
          <span style={{ fontWeight: '600', color: '#dc2626' }}>Sign Out</span>
        </button>
      </div>
    </div>
  </div>
);

// ============================================================
// MAIN EMPLOYEE PORTAL  — thin orchestrator only
// ============================================================
const EmployeePortal = () => {
  const navigate = useNavigate();
  const [currentView, setCurrentView] = useState('login');
  const [employee, setEmployee] = useState(null);
  const [dashboardData, setDashboardData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [clockStatus, setClockStatus] = useState(null);

  // Stable callback — no re-render loop
  const handleClockStatusChange = useCallback((status) => {
    setClockStatus(status);
  }, []);

  useEffect(() => { checkAuthStatus(); }, []);

  const checkAuthStatus = async () => {
    try {
      const token = localStorage.getItem('employee_access_token');
      if (!token) { setCurrentView('login'); return; }
      const response = await fetch('/api/documents/training/employee/dashboard', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({}),
      });
      if (response.ok) {
        const data = await response.json();
        setIsAuthenticated(true);
        setCurrentView('dashboard');
        processDashboardData(data);
      } else {
        localStorage.removeItem('employee_access_token');
        localStorage.removeItem('employee_refresh_token');
        setCurrentView('login');
      }
    } catch { setCurrentView('login'); }
  };

  const processDashboardData = (data) => {
    setDashboardData(data);
    setEmployee(prev => ({
      ...prev,
      id: data.employeeId || prev?.id,
      firstName: prev?.firstName || data.userName?.split(' ')[0] || 'Team Member',
      lastName: prev?.lastName || data.userName?.split(' ').slice(1).join(' ') || '',
      salonName: prev?.salonName || data.salonName || 'Your Salon',
      secureId: prev?.secureId || data.secureId,
    }));
  };

  const loadDashboardData = useCallback(async (token = null) => {
    setLoading(true);
    setError(null);
    try {
      const authToken = token || localStorage.getItem('employee_access_token');
      const response = await fetch('/api/documents/training/employee/dashboard', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${authToken}` },
        body: JSON.stringify({}),
      });
      const data = await response.json();
      if (response.ok && data.success) {
        processDashboardData(data);
      } else {
        setError('Failed to load dashboard data.');
      }
    } catch {
      setError('Failed to load dashboard data. Please try again.');
    } finally {
      setLoading(false);
    }
  }, []);

  // Called by LoginView on success
  const handleLoginSuccess = async (data) => {
    setEmployee({
      firstName: data.employee?.first_name || 'Team Member',
      lastName: data.employee?.last_name || '',
      salonName: data.employee?.salon_name || 'Your Salon',
      id: data.employee?.id,
      secureId: data.employee?.secure_id,
    });
    setIsAuthenticated(true);
    await loadDashboardData(data.access_token);
    setCurrentView('dashboard');
  };

  const handleLogout = async () => {
    try { await portalApi.post('/auth/logout'); } catch { /* ignore */ }
    localStorage.removeItem('employee_access_token');
    localStorage.removeItem('employee_refresh_token');
    setIsAuthenticated(false);
    setEmployee(null);
    setDashboardData(null);
    setCurrentView('login');
  };

  // Loading / error guards (only when authenticated)
  if (loading && isAuthenticated) {
    return (
      <div style={{ ...styles.container, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <style>{pulseAnimation}</style>
        <div style={{ textAlign: 'center' }}>
          <div style={{ width: '48px', height: '48px', border: '4px solid #e2e8f0', borderTopColor: '#0d9488', borderRadius: '50%', animation: 'spin 1s linear infinite', margin: '0 auto 16px' }} />
          <div style={{ color: '#64748b' }}>Loading your dashboard...</div>
        </div>
      </div>
    );
  }

  if (error && isAuthenticated) {
    return (
      <div style={{ ...styles.container, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '16px' }}>
        <div style={{ ...styles.card, textAlign: 'center', maxWidth: '400px' }}>
          <div style={{ fontSize: '48px', marginBottom: '16px' }}>⚠️</div>
          <h2 style={{ color: '#1e293b', marginBottom: '8px' }}>Something went wrong</h2>
          <p style={{ color: '#64748b', marginBottom: '16px' }}>{error}</p>
          <button onClick={loadDashboardData} style={styles.buttonPrimary}>Try Again</button>
        </div>
      </div>
    );
  }

  // Route to the correct view
  switch (currentView) {
    case 'login':
      return <LoginView onLoginSuccess={handleLoginSuccess} />;
    case 'dashboard':
      return (
        <DashboardView
          employee={employee}
          dashboardData={dashboardData}
          onNavigate={setCurrentView}
          onClockStatusChange={handleClockStatusChange}
        />
      );
    case 'timeoff':
      return <TimeOffHub onBack={() => setCurrentView('dashboard')} employeeData={employee} />;
    case 'flexibleworking':
      return <FlexibleWorkingHub onBack={() => setCurrentView('dashboard')} employeeData={employee} />;
    case 'holidays':
      return (
        <HolidaysView
          employee={employee}
          dashboardData={dashboardData}
          onBack={() => setCurrentView('dashboard')}
          onDataRefresh={loadDashboardData}
        />
      );
    case 'rota':
      return <RotaView onBack={() => setCurrentView('dashboard')} />;
    case 'messages':
      return <MessagesView onBack={() => setCurrentView('dashboard')} onDataRefresh={loadDashboardData} />;
    case 'documents':
      return <DocumentsView onBack={() => setCurrentView('dashboard')} onDataRefresh={loadDashboardData} />;
    case 'fitnote':
      return <FitNoteView employee={employee} onBack={() => setCurrentView('dashboard')} />;
    case 'targets':
      return <TargetsView onBack={() => setCurrentView('dashboard')} />;
    case 'profile':
      return <ProfileView employee={employee} onBack={() => setCurrentView('dashboard')} onLogout={handleLogout} />;
    case 'training':
      return <TrainingView onBack={() => setCurrentView('dashboard')} employeeData={employee} />;
    default:
      return (
        <DashboardView
          employee={employee}
          dashboardData={dashboardData}
          onNavigate={setCurrentView}
          onClockStatusChange={handleClockStatusChange}
        />
      );
  }
};

export default EmployeePortal;